/**
 * View Page - Read-only photo viewer
 *
 * A slim version of the Edit page for viewing photos without editing capabilities.
 * Uses shared components from features/photo/ for consistency with Edit page.
 *
 * Features:
 * - Filterable photo sidebar
 * - Image display (viz.jpg or edit.jpg)
 * - Viz/Edit toggle
 * - Crop mask toggle
 * - Full res button
 * - Player info bar (tagged persons) - read-only display
 * - Face overlays (view mode - no interactive buttons)
 *
 * Does NOT have (hidden, not disabled):
 * - Crop mode / editing buttons
 * - Mark done / Skip buttons
 * - Develop tab
 * - Face tagging controls
 */

import { useEffect, useState, useMemo } from 'react';
import { usePhotoViewer, PhotoViewerProvider, PhotoToolbar } from '../features/photo';
import { PhotoListSidebar, ImageDisplay } from '../features/edit/components';
import { FaceOverlay, FullResViewer } from '../components/edit';
import CropMask from '../shared/components/CropMask';
import PersonChips from '../shared/components/PersonChips';
import Spinner from '../shared/components/ui/Spinner';
import { getStatusLabel } from '../utils/statusUtils';

export default function View() {
  // Use shared photo viewer hook with View-specific storage prefix
  const viewer = usePhotoViewer({
    storagePrefix: 'matrix-view',
    autoSelectFirst: true,
    // View page shows all statuses (no default filter)
    defaultStatuses: null,
  });

  const {
    // Photo list
    photos,
    filteredPhotos,
    loadingList,
    loadPhotos,
    // Selected photo
    selectedPhotoId,
    selectedPhoto,
    metaDoc,
    faces,
    loadingPhoto,
    setSelectedPhotoId,
    // Display preferences
    showVizImage,
    toggleVizImage,
    showCropMask,
    toggleCropMask,
    // Image refs
    imgRef,
    imgWrapperRef,
    actualImgRef,
    imgBounds,
    vizNaturalDims,
    setVizNaturalDims,
    updateImgBounds,
    // Filters
    filters,
    toggleFilter,
    search,
    setSearch,
    sortOrder,
    setSortOrder,
    // Computed
    cropBox,
    currentVizUrl,
    currentEditedUrl,
    fullResUrl,
    taggedPersons,
    // Permissions (for delete button visibility)
    canDeletePhotos,
    // Navigation
    selectNextPhoto,
    selectPrevPhoto,
  } = viewer;

  // Full resolution modal state
  const [showFullRes, setShowFullRes] = useState(false);

  // Compute full-frame dimensions for face overlay positioning
  // Same logic as Edit.jsx - use metadata original dimensions, not rendered dimensions
  const imgDims = useMemo(() => {
    const sati = metaDoc?.pipeline?.sati;
    // Priority: sati.original_dims > face data > selectedPhoto
    const firstFace = faces[0];
    return {
      w: sati?.original_dims?.width || firstFace?.image_width || selectedPhoto?.image_width || firstFace?.meta?.image_width,
      h: sati?.original_dims?.height || firstFace?.image_height || selectedPhoto?.image_height || firstFace?.meta?.image_height,
    };
  }, [metaDoc, faces, selectedPhoto]);

  // Container dimensions for local-crop mode
  const [wrapperDims, setWrapperDims] = useState(null);

  // Track wrapper dimensions for ImageDisplay local-crop
  useEffect(() => {
    const container = imgWrapperRef.current;
    if (!container) return;
    const update = () => {
      const rect = container.getBoundingClientRect();
      setWrapperDims({ w: rect.width, h: rect.height });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(container);
    return () => ro.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPhotoId]);

  // Hovered person (for chip <-> face overlay highlight sync)
  const [hoveredPersonId, setHoveredPersonId] = useState(null);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Escape closes full-res modal
      if (e.key === 'Escape' && showFullRes) {
        e.preventDefault();
        setShowFullRes(false);
        return;
      }

      // ArrowUp = previous image
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        selectPrevPhoto();
        return;
      }

      // ArrowDown = next image
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        selectNextPhoto();
        return;
      }

      // V key = toggle viz/edit view
      if (e.key.toLowerCase() === 'v' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        toggleVizImage();
        return;
      }

      // M key = toggle crop mask
      if (e.key.toLowerCase() === 'm' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        toggleCropMask();
        return;
      }

      // F key = full res
      if (e.key.toLowerCase() === 'f' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        if (fullResUrl) setShowFullRes(true);
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showFullRes, selectPrevPhoto, selectNextPhoto, toggleVizImage, toggleCropMask, fullResUrl]);

  // Total face count (excluding ignored)
  const totalFaces = faces.filter(f => !f.ignored).length;

  // Filename for display
  const filename = selectedPhoto?.filename || 'No photo selected';

  // Show loading spinner while initial list loads
  if (loadingList && filteredPhotos.length === 0) {
    return (
      <div className="page page-view" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <PhotoViewerProvider value={{ ...viewer, mode: 'view' }}>
      <div className="page page-view">
        {/* Photo list sidebar */}
        <PhotoListSidebar
          photos={filteredPhotos}
          selectedImageId={selectedPhotoId}
          loadingList={loadingList}
          sortOrder={sortOrder}
          search={search}
          filters={filters}
          onSelectPhoto={setSelectedPhotoId}
          onSortChange={setSortOrder}
          onSearchChange={setSearch}
          onFilterToggle={toggleFilter}
          onClearFilters={() => viewer.setFilters([])}
          onRefresh={() => loadPhotos({ keepSelection: true })}
          getStatusLabel={getStatusLabel}
        />

        {/* Main content */}
        <main className="view-main">
          {/* Toolbar - View mode: no crop/approve/edit buttons */}
          <div className="card tag-photo">
            <PhotoToolbar
              filename={filename}
              isReadOnly={true}
              hasEditedUrl={!!selectedPhoto?.edited_url}
              // Display state
              showVizImage={showVizImage}
              onToggleViz={toggleVizImage}
              showCropMask={showCropMask}
              onToggleCropMask={toggleCropMask}
              // Full res action
              onFullRes={fullResUrl ? () => setShowFullRes(true) : undefined}
              // No crop mode in View
              cropMode={false}
              // No edit/approve actions in View (permissions set to false)
              canEditPhotos={false}
              canApprovePhotos={false}
              // Delete only if user has permission
              canDeletePhotos={canDeletePhotos}
              onDelete={canDeletePhotos ? () => {
                // Delete not implemented in View page yet
              } : undefined}
            />
          </div>

          {/* Image display */}
          <div className="viz-block">
            <div className="viz-frame tall" ref={imgRef}>
              <div className="img-wrapper" ref={imgWrapperRef}>
                {loadingPhoto ? (
                  <div className="image-loading-overlay">
                    <Spinner size="md" />
                  </div>
                ) : currentVizUrl ? (
                  <ImageDisplay
                    ref={actualImgRef}
                    currentVizUrl={currentVizUrl}
                    currentEditedUrl={currentEditedUrl}
                    showVizImage={showVizImage}
                    cropMode={false}
                    pendingCrop={null}
                    cropBox={cropBox}
                    metaDoc={metaDoc}
                    containerDims={wrapperDims}
                    onLoad={(e) => {
                      updateImgBounds();
                      if (e?.target && showVizImage) {
                        setVizNaturalDims({ w: e.target.naturalWidth, h: e.target.naturalHeight });
                      }
                    }}
                    onError={() => {}}
                  />
                ) : (
                  <div className="no-photo">Select a photo from the sidebar</div>
                )}

                {/* Crop mask overlay */}
                {showCropMask && cropBox && !loadingPhoto && (
                  <CropMask cropBox={cropBox} maskId="viewCropMask" />
                )}

                {/* Face overlays - view mode (read-only) */}
                {showVizImage && !loadingPhoto && imgBounds && (
                  <div
                    className="overlay"
                    style={{
                      position: 'absolute',
                      left: `${imgBounds.x}px`,
                      top: `${imgBounds.y}px`,
                      width: `${imgBounds.w}px`,
                      height: `${imgBounds.h}px`,
                    }}
                  >
                    {faces.filter(f => !f.ignored).map(face => (
                      <FaceOverlay
                        key={face.id}
                        face={face}
                        selected={false}
                        isHovered={hoveredPersonId && (face.person === hoveredPersonId || face.person_id === hoveredPersonId)}
                        onHover={(f) => setHoveredPersonId(f ? (f.person || f.person_id) : null)}
                        onSelect={() => {}}
                        dims={imgDims}
                        personLabel={face.person_name}
                        cropBox={cropBox}
                        viewMode={showVizImage ? 'viz' : 'edit'}
                        imgBounds={imgBounds}
                        vizNaturalDims={vizNaturalDims}
                        mode="view"
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Player info bar (read-only) */}
          <div className="view-player-bar">
            <span className="label">Tagged</span>
            <div className="divider" />
            <div className="persons">
              <PersonChips
                persons={taggedPersons}
                readOnly={true}
                emptyMessage="No persons tagged"
                hoveredPersonId={hoveredPersonId}
                onHover={setHoveredPersonId}
              />
            </div>
            {totalFaces > 0 && (
              <>
                <div className="divider" />
                <span className="face-count">{totalFaces} faces</span>
              </>
            )}
          </div>
        </main>

        {/* Full-res modal */}
        {showFullRes && fullResUrl && (
          <FullResViewer
            url={fullResUrl}
            onClose={() => setShowFullRes(false)}
          />
        )}
      </div>
    </PhotoViewerProvider>
  );
}
